import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { formatCurrency } from 'src/utils/currency';
import { formatDateISO } from 'src/utils/date';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },

  cardHeader: {
    color: theme.palette.secondary.main,
  },

  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },

  adjustmentTotal: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    marginLeft: '50px',
    color: theme.palette.common.black,

    '& span': {
      color: theme.palette.primary.light,
    },
  },

  adjustmentSummary: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },

  adjustmentSummaryMeta: {
    flexGrow: 1,
    paddingLeft: theme.spacing(3),
  },

  adjustmentSubHeaderLabel: {
    paddingLeft: '10px',
  },

  adjustmentSubHeaderValue: {
    color: theme.palette.secondary.main,
    paddingLeft: '5px',
  },

  adjustmentSummaryTotal: {
    paddingRight: theme.spacing(2),

    '& span': {
      color: theme.palette.primary.light,
    },
  },

  adjustmentDetails: {
    display: 'block',
    padding: 0,
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2, 0, 2),
  },

  sectionTitle: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
  },

  sectionTotal: {
    '& span': {
      color: theme.palette.primary.light,
    },
  },

  notes: {
    padding: theme.spacing(1, 2),
  },
}));

const ADJUSTMENT_ID_KEY = 'PurchaseOrderAdjustmentId';
const SECONDARY_CATEGORY_ID = '2';

function splitCamelCase(name) {
  return String(name).replace(/([A-Z])/g, ' $1').trim();
}

function getChargeTypeId(charge) {
  return charge?.ChargeTypeId;
}

function getChargeAmount(charge) {
  return Number(charge?.Amount ?? 0);
}

function sumCharges(charges) {
  return charges.reduce((total, charge) => total + getChargeAmount(charge), 0);
}

function getAdjustmentTotal(adjustment, charges) {
  return adjustment?.TotalAmount != null
    ? Number(adjustment.TotalAmount)
    : sumCharges(charges);
}

// CategoryId now arrives inline on each charge from the sproc, which inner-joins the charge-type table so a charge always has a CategoryId of 1 or 2.
function buildChargeSections(charges) {
  const providerCharges = [];
  const secondaryCharges = [];

  charges.forEach((charge) => {
    const isSecondary = String(charge?.CategoryId) === SECONDARY_CATEGORY_ID;
    if (isSecondary) {
      secondaryCharges.push(charge);
    } else {
      providerCharges.push(charge);
    }
  });

  return [
    { label: 'Provider Charges', charges: providerCharges },
    { label: 'Secondary Charges', charges: secondaryCharges },
  ].filter((section) => section.charges.length > 0);
}

function AdjustmentInfo({ adjustments, adjustmentCharges }) {
  const classes = useStyles();

  const chargesByAdjustmentId = useMemo(() => {
    const groupedCharges = new Map();
    (adjustmentCharges ?? []).forEach((charge) => {
      const adjustmentId = String(charge?.[ADJUSTMENT_ID_KEY]);
      if (!groupedCharges.has(adjustmentId)) {
        groupedCharges.set(adjustmentId, []);
      }
      groupedCharges.get(adjustmentId).push(charge);
    });
    return groupedCharges;
  }, [adjustmentCharges]);

  const adjustmentsTotal = useMemo(
    () => (adjustments ?? []).reduce(
      (total, adjustment) => total + getAdjustmentTotal(
        adjustment,
        chargesByAdjustmentId.get(String(adjustment?.[ADJUSTMENT_ID_KEY])) ?? []
      ),
      0
    ),
    [adjustments, chargesByAdjustmentId]
  );

  // Quantity is behind a feature flag in Cash Qore, so the column is only present some of the time.
  const isQuantityVisible = useMemo(
    () => (adjustmentCharges ?? []).some((charge) => charge?.Quantity != null),
    [adjustmentCharges]
  );

  if (!adjustments?.length) {
    return null;
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title={(
          <>
            Adjustments
            <span className={classes.adjustmentTotal}>
              Total Adjustment Amount: <span>{formatCurrency(adjustmentsTotal)}</span>
            </span>
          </>
        )}
      />
      <Divider />
      {adjustments.map((adjustment) => {
        const adjustmentId = adjustment?.[ADJUSTMENT_ID_KEY];
        const charges = chargesByAdjustmentId.get(String(adjustmentId)) ?? [];
        const chargeSections = buildChargeSections(charges);

        return (
          <Accordion key={adjustmentId}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className={classes.adjustmentSummary}>
                <Typography variant="body2" className={classes.fontWeightMedium}>
                  {`Adjustment ${adjustmentId}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.adjustmentSummaryMeta}
                >
                  {adjustment?.Type && (
                    <>
                      <span className={classes.adjustmentSubHeaderLabel}>Type</span>
                      <span className={classes.adjustmentSubHeaderValue}>{adjustment.Type}</span>
                    </>
                  )}
                  {adjustment?.StatusDescription && (
                    <>
                      <span className={classes.adjustmentSubHeaderLabel}>Status</span>
                      <span className={classes.adjustmentSubHeaderValue}>
                        {adjustment.StatusDescription}
                      </span>
                    </>
                  )}
                  {adjustment?.DateCreated && (
                    <>
                      <span className={classes.adjustmentSubHeaderLabel}>Created</span>
                      <span className={classes.adjustmentSubHeaderValue}>
                        {formatDateISO(adjustment.DateCreated)}
                      </span>
                    </>
                  )}
                </Typography>
                <Typography variant="body2" className={classes.adjustmentSummaryTotal}>
                  Total: <span>{formatCurrency(getAdjustmentTotal(adjustment, charges))}</span>
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.adjustmentDetails}>
              {adjustment?.Notes && (
                <Typography variant="body2" color="textSecondary" className={classes.notes}>
                  {adjustment.Notes}
                </Typography>
              )}
              {chargeSections.length === 0 && (
                <Typography variant="body2" color="textSecondary" className={classes.notes}>
                  No charges on this adjustment
                </Typography>
              )}
              {chargeSections.map((section) => (
                <div key={section.label}>
                  <div className={classes.sectionHeader}>
                    <Typography variant="body2" className={classes.sectionTitle}>
                      {section.label}
                    </Typography>
                    <Typography variant="body2" className={classes.sectionTotal}>
                      Total: <span>{formatCurrency(sumCharges(section.charges))}</span>
                    </Typography>
                  </div>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Charge</TableCell>
                        {isQuantityVisible && <TableCell align="right">Quantity</TableCell>}
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.charges.map((charge) => (
                        <TableRow key={charge.PurchaseOrderAdjustmentChargeId}>
                          <TableCell className={classes.fontWeightMedium}>
                            {charge.PurchaseOrderChargeTypeName
                              ? splitCamelCase(charge.PurchaseOrderChargeTypeName)
                              : `Charge Type ${getChargeTypeId(charge)}`}
                          </TableCell>
                          {isQuantityVisible && (
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {charge.Quantity ?? '-'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrency(getChargeAmount(charge))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Card>
  );
}

AdjustmentInfo.propTypes = {
  adjustments: PropTypes.arrayOf(PropTypes.shape({
    PurchaseOrderAdjustmentId: PropTypes.number,
    PurchaseOrderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    Type: PropTypes.string,
    StatusDescription: PropTypes.string,
    TotalAmount: PropTypes.number,
    Notes: PropTypes.string,
    DateCreated: PropTypes.string,
  })),
  adjustmentCharges: PropTypes.arrayOf(PropTypes.shape({
    PurchaseOrderAdjustmentId: PropTypes.number,
    PurchaseOrderAdjustmentChargeId: PropTypes.number,
    ChargeTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    PurchaseOrderChargeTypeName: PropTypes.string,
    CategoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    Amount: PropTypes.number,
    Quantity: PropTypes.number,
  })),
};

AdjustmentInfo.defaultProps = {
  adjustments: [],
  adjustmentCharges: [],
};

export default AdjustmentInfo;
